/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
/* eslint-disable @typescript-eslint/naming-convention */
// AUTO-GENERATED by scripts/generateUnitsJson.ts. Do not edit by hand.
import type { NestedValueOf, ValueOf } from "@itwin/core-bentley";

/** Canonical bundled unit identifiers grouped by phenomenon from the bundled Units.json asset.
 * @beta
 */
export const Units = {
  ACCELERATION: {
    CM_PER_SEC_SQ: "Units.CM_PER_SEC_SQ",
    FT_PER_SEC_SQ: "Units.FT_PER_SEC_SQ",
    M_PER_SEC_SQ: "Units.M_PER_SEC_SQ",
  },
  ANGLE: {
    ARC_DEG: "Units.ARC_DEG",
    ARC_MINUTE: "Units.ARC_MINUTE",
    ARC_QUADRANT: "Units.ARC_QUADRANT",
    ARC_SECOND: "Units.ARC_SECOND",
    GRAD: "Units.GRAD",
    RAD: "Units.RAD",
    REVOLUTION: "Units.REVOLUTION",
  },
  ANGULAR_VELOCITY: {
    DEG_PER_HR: "Units.DEG_PER_HR",
    DEG_PER_MIN: "Units.DEG_PER_MIN",
    DEG_PER_SEC: "Units.DEG_PER_SEC",
    RAD_PER_HR: "Units.RAD_PER_HR",
    RAD_PER_MIN: "Units.RAD_PER_MIN",
    RAD_PER_SEC: "Units.RAD_PER_SEC",
    RPH: "Units.RPH",
    RPM: "Units.RPM",
    RPS: "Units.RPS",
  },
  APPARENT_POWER: {
    KVA: "Units.KVA",
    MVA: "Units.MVA",
    VA: "Units.VA",
  },
  AREA: {
    ACRE: "Units.ACRE",
    ARE: "Units.ARE",
    HECTARE: "Units.HECTARE",
    SQ_CHAIN: "Units.SQ_CHAIN",
    SQ_CM: "Units.SQ_CM",
    SQ_DM: "Units.SQ_DM",
    SQ_FT: "Units.SQ_FT",
    SQ_IN: "Units.SQ_IN",
    SQ_KM: "Units.SQ_KM",
    SQ_M: "Units.SQ_M",
    SQ_MILE: "Units.SQ_MILE",
    SQ_MM: "Units.SQ_MM",
    SQ_UM: "Units.SQ_UM",
    SQ_US_SURVEY_CHAIN: "Units.SQ_US_SURVEY_CHAIN",
    SQ_US_SURVEY_FT: "Units.SQ_US_SURVEY_FT",
    SQ_US_SURVEY_IN: "Units.SQ_US_SURVEY_IN",
    SQ_US_SURVEY_MILE: "Units.SQ_US_SURVEY_MILE",
    SQ_US_SURVEY_YRD: "Units.SQ_US_SURVEY_YRD",
    SQ_YRD: "Units.SQ_YRD",
    THOUSAND_SQ_FT: "Units.THOUSAND_SQ_FT",
    US_SURVEY_ACRE: "Units.US_SURVEY_ACRE",
  },
  AREA_MOMENT_INERTIA: {
    CM_TO_THE_FOURTH: "Units.CM_TO_THE_FOURTH",
    FT_TO_THE_FOURTH: "Units.FT_TO_THE_FOURTH",
    IN_TO_THE_FOURTH: "Units.IN_TO_THE_FOURTH",
    M_TO_THE_FOURTH: "Units.M_TO_THE_FOURTH",
    MM_TO_THE_FOURTH: "Units.MM_TO_THE_FOURTH",
  },
  AREA_SPRING_CONSTANT: {
    AREA_SPRING_CONSTANT_KN_PER_CUB_M: "Units.AREA_SPRING_CONSTANT_KN_PER_CUB_M",
    AREA_SPRING_CONSTANT_KPF_PER_CUB_FT: "Units.AREA_SPRING_CONSTANT_KPF_PER_CUB_FT",
    AREA_SPRING_CONSTANT_N_PER_CUB_M: "Units.AREA_SPRING_CONSTANT_N_PER_CUB_M",
  },
  AREA_TORQUE: {
    KN_M_PER_SQ_M: "Units.KN_M_PER_SQ_M",
    KPF_FT_PER_SQ_FT: "Units.KPF_FT_PER_SQ_FT",
    LBF_FT_PER_SQ_FT: "Units.LBF_FT_PER_SQ_FT",
    LBF_IN_PER_SQ_IN: "Units.LBF_IN_PER_SQ_IN",
    MEGAN_M_PER_SQ_M: "Units.MEGAN_M_PER_SQ_M",
    N_CM_PER_SQ_CM: "Units.N_CM_PER_SQ_CM",
    N_M_PER_SQ_M: "Units.N_M_PER_SQ_M",
  },
  CAPITA: {
    HUNDRED_PERSON: "Units.HUNDRED_PERSON",
    PERSON: "Units.PERSON",
    THOUSAND_PERSON: "Units.THOUSAND_PERSON",
  },
  CURRENCY: {
    MONETARY_UNIT: "Units.MONETARY_UNIT",
    US_DOLLAR: "Units.US_DOLLAR",
  },
  CURRENT: {
    A: "Units.A",
    KILOAMPERE: "Units.KILOAMPERE",
    MICROAMPERE: "Units.MICROAMPERE",
    MILLIAMPERE: "Units.MILLIAMPERE",
  },
  DENSITY: {
    G_PER_CUB_CM: "Units.G_PER_CUB_CM",
    KG_PER_CUB_CM: "Units.KG_PER_CUB_CM",
    KG_PER_CUB_M: "Units.KG_PER_CUB_M",
    KG_PER_LITRE: "Units.KG_PER_LITRE",
    KIP_PER_CUB_FT: "Units.KIP_PER_CUB_FT",
    LBM_PER_CUB_FT: "Units.LBM_PER_CUB_FT",
    LBM_PER_CUB_IN: "Units.LBM_PER_CUB_IN",
    LBM_PER_GALLON: "Units.LBM_PER_GALLON",
    LBM_PER_GALLON_IMPERIAL: "Units.LBM_PER_GALLON_IMPERIAL",
    LBM_PER_MILLION_GALLON: "Units.LBM_PER_MILLION_GALLON",
    MG_PER_LITRE: "Units.MG_PER_LITRE",
    MKG_PER_LITRE: "Units.MKG_PER_LITRE",
    SHORT_TON_PER_CUB_FT: "Units.SHORT_TON_PER_CUB_FT",
    SLUG_PER_CUB_FT: "Units.SLUG_PER_CUB_FT",
  },
  DYNAMIC_VISCOSITY: {
    CENTIPOISE: "Units.CENTIPOISE",
    LBM_PER_FT_S: "Units.LBM_PER_FT_S",
    PA_S: "Units.PA_S",
    POISE: "Units.POISE",
  },
  ELECTRIC_CHARGE: {
    COULOMB: "Units.COULOMB",
  },
  ELECTRIC_POTENTIAL: {
    KILOVOLT: "Units.KILOVOLT",
    MEGAVOLT: "Units.MEGAVOLT",
    VOLT: "Units.VOLT",
  },
  ELECTRICAL_RESISTANCE: {
    KOHM: "Units.KOHM",
    OHM: "Units.OHM",
  },
  ELECTRICAL_RESISTIVITY: {
    OHM_M: "Units.OHM_M",
  },
  ENERGY_DENSITY: {
    J_PER_CUB_M: "Units.J_PER_CUB_M",
    KJ_PER_CUB_M: "Units.KJ_PER_CUB_M",
    KWH_PER_CUB_FT: "Units.KWH_PER_CUB_FT",
    KWH_PER_CUB_M: "Units.KWH_PER_CUB_M",
    KWH_PER_MILLION_GALLON: "Units.KWH_PER_MILLION_GALLON",
  },
  ENTROPY: {
    BTU_PER_FAHRENHEIT: "Units.BTU_PER_FAHRENHEIT",
    J_PER_K: "Units.J_PER_K",
    KJ_PER_K: "Units.KJ_PER_K",
  },
  FLOW_DENSITY_PER_AREA: {
    CUB_FT_PER_ACRE_SEC: "Units.CUB_FT_PER_ACRE_SEC",
    CUB_FT_PER_SQ_FT_MIN: "Units.CUB_FT_PER_SQ_FT_MIN",
    CUB_FT_PER_SQ_FT_SEC: "Units.CUB_FT_PER_SQ_FT_SEC",
    CUB_FT_PER_SQ_MILE_SEC: "Units.CUB_FT_PER_SQ_MILE_SEC",
    CUB_M_PER_HECTARE_DAY: "Units.CUB_M_PER_HECTARE_DAY",
    CUB_M_PER_SQ_KM_DAY: "Units.CUB_M_PER_SQ_KM_DAY",
    CUB_M_PER_SQ_M_DAY: "Units.CUB_M_PER_SQ_M_DAY",
    CUB_M_PER_SQ_M_SEC: "Units.CUB_M_PER_SQ_M_SEC",
    GALLON_PER_ACRE_DAY: "Units.GALLON_PER_ACRE_DAY",
    GALLON_PER_ACRE_MIN: "Units.GALLON_PER_ACRE_MIN",
    GALLON_PER_SQ_FT_DAY: "Units.GALLON_PER_SQ_FT_DAY",
    GALLON_PER_SQ_FT_MIN: "Units.GALLON_PER_SQ_FT_MIN",
    GALLON_PER_SQ_MILE_DAY: "Units.GALLON_PER_SQ_MILE_DAY",
    GALLON_PER_SQ_MILE_MIN: "Units.GALLON_PER_SQ_MILE_MIN",
    LITRE_PER_SQ_M_SEC: "Units.LITRE_PER_SQ_M_SEC",
  },
  FORCE: {
    DECAN: "Units.DECAN",
    DYNE: "Units.DYNE",
    KGF: "Units.KGF",
    KN: "Units.KN",
    KPF: "Units.KPF",
    LBF: "Units.LBF",
    LONG_TON_FORCE: "Units.LONG_TON_FORCE",
    MEGAN: "Units.MEGAN",
    MN: "Units.MN",
    N: "Units.N",
    OZF: "Units.OZF",
    PDL: "Units.PDL",
    SHORT_TON_FORCE: "Units.SHORT_TON_FORCE",
  },
  FORCE_DENSITY: {
    KN_PER_CUB_FT: "Units.KN_PER_CUB_FT",
    KN_PER_CUB_M: "Units.KN_PER_CUB_M",
    KPF_PER_CUB_FT: "Units.KPF_PER_CUB_FT",
    N_PER_CUB_FT: "Units.N_PER_CUB_FT",
    N_PER_CUB_M: "Units.N_PER_CUB_M",
  },
  FORCE_DISPLAY_SCALE: {
    M_PER_KN: "Units.M_PER_KN",
    M_PER_N: "Units.M_PER_N",
  },
  FREQUENCY: {
    HZ: "Units.HZ",
    KHZ: "Units.KHZ",
    MHZ: "Units.MHZ",
  },
  HEAT_FLUX_DENSITY: {
    W_PER_SQ_M: "Units.W_PER_SQ_M",
  },
  HEAT_TRANSFER: {
    BTU_PER_SQ_FT_HR_FAHRENHEIT: "Units.BTU_PER_SQ_FT_HR_FAHRENHEIT",
    W_PER_SQ_M_CELSIUS: "Units.W_PER_SQ_M_CELSIUS",
    W_PER_SQ_M_K: "Units.W_PER_SQ_M_K",
  },
  HORIZONTAL_DIRECTION: {
    HORIZONTAL_DIR_ARC_DEG: "Units.HORIZONTAL_DIR_ARC_DEG",
    HORIZONTAL_DIR_ARC_MINUTE: "Units.HORIZONTAL_DIR_ARC_MINUTE",
    HORIZONTAL_DIR_ARC_SECOND: "Units.HORIZONTAL_DIR_ARC_SECOND",
    HORIZONTAL_DIR_GRAD: "Units.HORIZONTAL_DIR_GRAD",
    HORIZONTAL_DIR_MILLIRAD: "Units.HORIZONTAL_DIR_MILLIRAD",
    HORIZONTAL_DIR_RAD: "Units.HORIZONTAL_DIR_RAD",
    HORIZONTAL_DIR_REVOLUTION: "Units.HORIZONTAL_DIR_REVOLUTION",
  },
  ILLUMINANCE: {
    LUMEN_PER_SQ_FT: "Units.LUMEN_PER_SQ_FT",
    LUX: "Units.LUX",
  },
  KINEMATIC_VISCOSITY: {
    CENTISTOKE: "Units.CENTISTOKE",
    SQ_FT_PER_SEC: "Units.SQ_FT_PER_SEC",
    SQ_M_PER_SEC: "Units.SQ_M_PER_SEC",
    STOKE: "Units.STOKE",
  },
  LENGTH: {
    CHAIN: "Units.CHAIN",
    CM: "Units.CM",
    DM: "Units.DM",
    FT: "Units.FT",
    IN: "Units.IN",
    KM: "Units.KM",
    M: "Units.M",
    MICROINCH: "Units.MICROINCH",
    MILE: "Units.MILE",
    MILLIFOOT: "Units.MILLIFOOT",
    MILLIINCH: "Units.MILLIINCH",
    MM: "Units.MM",
    NAUT_MILE: "Units.NAUT_MILE",
    UM: "Units.UM",
    US_SURVEY_CHAIN: "Units.US_SURVEY_CHAIN",
    US_SURVEY_FT: "Units.US_SURVEY_FT",
    US_SURVEY_IN: "Units.US_SURVEY_IN",
    US_SURVEY_MILE: "Units.US_SURVEY_MILE",
    US_SURVEY_YRD: "Units.US_SURVEY_YRD",
    YRD: "Units.YRD",
  },
  LINEAR_COEFFICIENT_OF_THERMAL_EXPANSION: {
    STRAIN_PER_CELSIUS: "Units.STRAIN_PER_CELSIUS",
    STRAIN_PER_FAHRENHEIT: "Units.STRAIN_PER_FAHRENHEIT",
    STRAIN_PER_KELVIN: "Units.STRAIN_PER_KELVIN",
    STRAIN_PER_RANKINE: "Units.STRAIN_PER_RANKINE",
  },
  LINEAR_DENSITY: {
    KG_PER_M: "Units.KG_PER_M",
    KG_PER_MM: "Units.KG_PER_MM",
    LBM_PER_FT: "Units.LBM_PER_FT",
  },
  LINEAR_LOAD: {
    KGF_PER_M: "Units.KGF_PER_M",
    KN_PER_M: "Units.KN_PER_M",
    KPF_PER_FT: "Units.KPF_PER_FT",
    LBF_PER_FT: "Units.LBF_PER_FT",
    LBF_PER_IN: "Units.LBF_PER_IN",
    MEGAN_PER_M: "Units.MEGAN_PER_M",
    N_PER_M: "Units.N_PER_M",
    N_PER_MM: "Units.N_PER_MM",
  },
  LINEAR_RATE: {
    PER_FT: "Units.PER_FT",
    PER_KM: "Units.PER_KM",
    PER_M: "Units.PER_M",
    PER_MILE: "Units.PER_MILE",
    PER_MM: "Units.PER_MM",
    PER_THOUSAND_FT: "Units.PER_THOUSAND_FT",
  },
  LINEAR_ROTATIONAL_SPRING_CONSTANT: {
    KN_PER_RAD: "Units.KN_PER_RAD",
    KPF_PER_RAD: "Units.KPF_PER_RAD",
    N_PER_RAD: "Units.N_PER_RAD",
  },
  LINEAR_SPRING_CONSTANT: {
    LINEAR_SPRING_CONSTANT_KN_PER_SQ_M: "Units.LINEAR_SPRING_CONSTANT_KN_PER_SQ_M",
    LINEAR_SPRING_CONSTANT_KPF_PER_SQ_FT: "Units.LINEAR_SPRING_CONSTANT_KPF_PER_SQ_FT",
    LINEAR_SPRING_CONSTANT_N_PER_SQ_M: "Units.LINEAR_SPRING_CONSTANT_N_PER_SQ_M",
  },
  LINEAR_TORQUE: {
    KN_M_PER_M: "Units.KN_M_PER_M",
    KPF_FT_PER_FT: "Units.KPF_FT_PER_FT",
    LBF_FT_PER_FT: "Units.LBF_FT_PER_FT",
    LBF_IN_PER_IN: "Units.LBF_IN_PER_IN",
    MEGAN_M_PER_M: "Units.MEGAN_M_PER_M",
    N_CM_PER_CM: "Units.N_CM_PER_CM",
    N_M_PER_M: "Units.N_M_PER_M",
  },
  LUMINOSITY: {
    CD: "Units.CD",
  },
  LUMINOUS_EFFICACY: {
    LUMEN_PER_W: "Units.LUMEN_PER_W",
  },
  LUMINOUS_FLUX: {
    LUMEN: "Units.LUMEN",
  },
  MASS: {
    G: "Units.G",
    GRM: "Units.GRM",
    KG: "Units.KG",
    KIPM: "Units.KIPM",
    LBM: "Units.LBM",
    LONG_TON_MASS: "Units.LONG_TON_MASS",
    MEGAGRAM: "Units.MEGAGRAM",
    MG: "Units.MG",
    MKG: "Units.MKG",
    NG: "Units.NG",
    OZM: "Units.OZM",
    SHORT_TON_MASS: "Units.SHORT_TON_MASS",
    SLUG: "Units.SLUG",
    TONNE: "Units.TONNE",
  },
  MASS_FLOW: {
    G_PER_HR: "Units.G_PER_HR",
    G_PER_MIN: "Units.G_PER_MIN",
    G_PER_SEC: "Units.G_PER_SEC",
    KG_PER_DAY: "Units.KG_PER_DAY",
    KG_PER_HR: "Units.KG_PER_HR",
    KG_PER_MIN: "Units.KG_PER_MIN",
    KG_PER_SEC: "Units.KG_PER_SEC",
    LBM_PER_DAY: "Units.LBM_PER_DAY",
    LBM_PER_HR: "Units.LBM_PER_HR",
    LBM_PER_MIN: "Units.LBM_PER_MIN",
    LBM_PER_SEC: "Units.LBM_PER_SEC",
    MG_PER_DAY: "Units.MG_PER_DAY",
    MG_PER_HR: "Units.MG_PER_HR",
    MG_PER_MIN: "Units.MG_PER_MIN",
    MG_PER_SEC: "Units.MG_PER_SEC",
    MKG_PER_DAY: "Units.MKG_PER_DAY",
    MKG_PER_HR: "Units.MKG_PER_HR",
    MKG_PER_MIN: "Units.MKG_PER_MIN",
    MKG_PER_SEC: "Units.MKG_PER_SEC",
    SHORT_TON_PER_HR: "Units.SHORT_TON_PER_HR",
    TONNE_PER_HR: "Units.TONNE_PER_HR",
  },
  MASS_RATIO: {
    GRM_PER_LBM: "Units.GRM_PER_LBM",
    KG_PER_KG: "Units.KG_PER_KG",
  },
  MOLAR_CONCENTRATION: {
    KMOL_PER_CUB_M: "Units.KMOL_PER_CUB_M",
    MICROMOL_PER_CUB_DM: "Units.MICROMOL_PER_CUB_DM",
    MOL_PER_CUB_DM: "Units.MOL_PER_CUB_DM",
    MOL_PER_CUB_FT: "Units.MOL_PER_CUB_FT",
    MOL_PER_CUB_M: "Units.MOL_PER_CUB_M",
    NMOL_PER_CUB_DM: "Units.NMOL_PER_CUB_DM",
    PICOMOL_PER_CUB_DM: "Units.PICOMOL_PER_CUB_DM",
  },
  MOLAR_FLOW: {
    KMOL_PER_SEC: "Units.KMOL_PER_SEC",
    MOL_PER_SEC: "Units.MOL_PER_SEC",
  },
  MOLAR_VOLUME: {
    CUB_FT_PER_LB_MOL: "Units.CUB_FT_PER_LB_MOL",
    CUB_M_PER_KMOL: "Units.CUB_M_PER_KMOL",
    CUB_M_PER_MOL: "Units.CUB_M_PER_MOL",
  },
  MOLE: {
    KMOL: "Units.KMOL",
    LB_MOL: "Units.LB_MOL",
    MOL: "Units.MOL",
  },
  MOMENT_DISPLAY_SCALE: {
    INVERSE_KN: "Units.INVERSE_KN",
    INVERSE_KPF: "Units.INVERSE_KPF",
    INVERSE_N: "Units.INVERSE_N",
  },
  NUMBER: {
    COEFFICIENT: "Units.COEFFICIENT",
    ONE: "Units.ONE",
  },
  PERCENTAGE: {
    DECIMAL_PERCENT: "Units.DECIMAL_PERCENT",
    PERCENT: "Units.PERCENT",
  },
  PIPE_DIAMETER_LENGTH: {
    FT_FT: "Units.FT_FT",
    FT_MILE: "Units.FT_MILE",
    IN_FT: "Units.IN_FT",
    IN_M: "Units.IN_M",
    IN_MILE: "Units.IN_MILE",
    M_KM: "Units.M_KM",
    M_M: "Units.M_M",
    MM_KM: "Units.MM_KM",
    MM_M: "Units.MM_M",
    MM_MILE: "Units.MM_MILE",
  },
  POPULATION_DENSITY: {
    PERSON_PER_ACRE: "Units.PERSON_PER_ACRE",
    PERSON_PER_HECTARE: "Units.PERSON_PER_HECTARE",
    PERSON_PER_SQ_FT: "Units.PERSON_PER_SQ_FT",
    PERSON_PER_SQ_KM: "Units.PERSON_PER_SQ_KM",
    PERSON_PER_SQ_M: "Units.PERSON_PER_SQ_M",
    PERSON_PER_SQ_MILE: "Units.PERSON_PER_SQ_MILE",
  },
  POWER: {
    BTU_PER_HR: "Units.BTU_PER_HR",
    GW: "Units.GW",
    HP: "Units.HP",
    KILOBTU_PER_HR: "Units.KILOBTU_PER_HR",
    KW: "Units.KW",
    MEGAW: "Units.MEGAW",
    W: "Units.W",
  },
  PRESSURE: {
    AT: "Units.AT",
    AT_GAUGE: "Units.AT_GAUGE",
    ATM: "Units.ATM",
    BAR: "Units.BAR",
    BAR_GAUGE: "Units.BAR_GAUGE",
    BARYE: "Units.BARYE",
    FT_H2O: "Units.FT_H2O",
    HECTOPASCAL: "Units.HECTOPASCAL",
    IN_H2O_AT_32F: "Units.IN_H2O_AT_32F",
    IN_H2O_AT_39_2F: "Units.IN_H2O_AT_39_2F",
    IN_H2O_AT_60F: "Units.IN_H2O_AT_60F",
    IN_HG: "Units.IN_HG",
    IN_HG_AT_32F: "Units.IN_HG_AT_32F",
    IN_HG_AT_60F: "Units.IN_HG_AT_60F",
    KGF_PER_SQ_M: "Units.KGF_PER_SQ_M",
    KILOPASCAL: "Units.KILOPASCAL",
    KILOPASCAL_GAUGE: "Units.KILOPASCAL_GAUGE",
    KSF: "Units.KSF",
    KSI: "Units.KSI",
    LBF_PER_SQ_FT: "Units.LBF_PER_SQ_FT",
    M_H2O: "Units.M_H2O",
    MBAR: "Units.MBAR",
    MEGAPASCAL: "Units.MEGAPASCAL",
    MEGAPASCAL_GAUGE: "Units.MEGAPASCAL_GAUGE",
    MICROPASCAL: "Units.MICROPASCAL",
    MM_H2O: "Units.MM_H2O",
    MM_HG_AT_32F: "Units.MM_HG_AT_32F",
    PA: "Units.PA",
    PA_GAUGE: "Units.PA_GAUGE",
    PSF: "Units.PSF",
    PSI: "Units.PSI",
    PSIG: "Units.PSIG",
    TORR: "Units.TORR",
  },
  PRESSURE_GRADIENT: {
    BAR_PER_KM: "Units.BAR_PER_KM",
    PA_PER_M: "Units.PA_PER_M",
  },
  PROBABILITY: {
    PROBABILITY_FRACTION: "Units.PROBABILITY_FRACTION",
    PROBABILITY_PERCENT: "Units.PROBABILITY_PERCENT",
  },
  ROTATIONAL_SPRING_CONSTANT: {
    KN_M_PER_RAD: "Units.KN_M_PER_RAD",
    KPF_FT_PER_RAD: "Units.KPF_FT_PER_RAD",
    N_M_PER_DEG: "Units.N_M_PER_DEG",
    N_M_PER_RAD: "Units.N_M_PER_RAD",
  },
  SLOPE: {
    CM_PER_M: "Units.CM_PER_M",
    FT_HORIZONTAL_PER_FT_VERTICAL: "Units.FT_HORIZONTAL_PER_FT_VERTICAL",
    FT_PER_FT: "Units.FT_PER_FT",
    FT_PER_IN: "Units.FT_PER_IN",
    FT_PER_MILE: "Units.FT_PER_MILE",
    FT_PER_THOUSAND_FOOT: "Units.FT_PER_THOUSAND_FOOT",
    HORIZONTAL_PER_VERTICAL: "Units.HORIZONTAL_PER_VERTICAL",
    IN_PER_FT: "Units.IN_PER_FT",
    M_HORIZONTAL_PER_M_VERTICAL: "Units.M_HORIZONTAL_PER_M_VERTICAL",
    M_PER_KM: "Units.M_PER_KM",
    M_PER_M: "Units.M_PER_M",
    MM_PER_M: "Units.MM_PER_M",
    PERCENT_SLOPE: "Units.PERCENT_SLOPE",
    VERTICAL_PER_HORIZONTAL: "Units.VERTICAL_PER_HORIZONTAL",
  },
  SOLIDANGLE: {
    STERAD: "Units.STERAD",
  },
  SPECIFIC_ENERGY: {
    BTU_PER_LBM: "Units.BTU_PER_LBM",
    J_PER_KG: "Units.J_PER_KG",
    KJ_PER_KG: "Units.KJ_PER_KG",
    MEGAJ_PER_KG: "Units.MEGAJ_PER_KG",
  },
  SPECIFIC_HEAT_CAPACITY: {
    BTU_PER_LBM_RANKINE: "Units.BTU_PER_LBM_RANKINE",
    J_PER_G_CELSIUS: "Units.J_PER_G_CELSIUS",
    J_PER_KG_K: "Units.J_PER_KG_K",
  },
  SPECIFIC_HEAT_CAPACITY_MOLAR: {
    BTU_PER_LB_MOL_RANKINE: "Units.BTU_PER_LB_MOL_RANKINE",
    J_PER_KMOL_K: "Units.J_PER_KMOL_K",
    J_PER_MOL_K: "Units.J_PER_MOL_K",
    KJ_PER_KMOL_K: "Units.KJ_PER_KMOL_K",
  },
  SPECIFIC_VOLUME: {
    CUB_FT_PER_LBM: "Units.CUB_FT_PER_LBM",
    CUB_M_PER_KG: "Units.CUB_M_PER_KG",
  },
  SPRING_CONSTANT: {
    SPRING_CONSTANT_KN_PER_M: "Units.SPRING_CONSTANT_KN_PER_M",
    SPRING_CONSTANT_KPF_PER_FT: "Units.SPRING_CONSTANT_KPF_PER_FT",
    SPRING_CONSTANT_N_PER_M: "Units.SPRING_CONSTANT_N_PER_M",
  },
  SURFACE_DENSITY: {
    G_PER_SQ_M: "Units.G_PER_SQ_M",
    KG_PER_HECTARE: "Units.KG_PER_HECTARE",
    KG_PER_SQ_M: "Units.KG_PER_SQ_M",
    LBM_PER_ACRE: "Units.LBM_PER_ACRE",
  },
  TEMPERATURE: {
    CELSIUS: "Units.CELSIUS",
    FAHRENHEIT: "Units.FAHRENHEIT",
    K: "Units.K",
    RANKINE: "Units.RANKINE",
  },
  TEMPERATURE_CHANGE: {
    DELTA_CELSIUS: "Units.DELTA_CELSIUS",
    DELTA_FAHRENHEIT: "Units.DELTA_FAHRENHEIT",
    DELTA_KELVIN: "Units.DELTA_KELVIN",
    DELTA_RANKINE: "Units.DELTA_RANKINE",
  },
  TEMPERATURE_GRADIENT: {
    KELVIN_PER_M: "Units.KELVIN_PER_M",
  },
  THERMAL_CONDUCTIVITY: {
    BTU_IN_PER_SQ_FT_HR_FAHRENHEIT: "Units.BTU_IN_PER_SQ_FT_HR_FAHRENHEIT",
    W_PER_M_C: "Units.W_PER_M_C",
    W_PER_M_K: "Units.W_PER_M_K",
  },
  THERMAL_INSULANCE: {
    SQ_FT_HR_FAHRENHEIT_PER_BTU: "Units.SQ_FT_HR_FAHRENHEIT_PER_BTU",
    SQ_M_CELSIUS_PER_WATT: "Units.SQ_M_CELSIUS_PER_WATT",
    SQ_M_KELVIN_PER_WATT: "Units.SQ_M_KELVIN_PER_WATT",
  },
  THREAD_PITCH: {
    IN_PER_DEGREE: "Units.IN_PER_DEGREE",
    M_PER_RAD: "Units.M_PER_RAD",
    M_PER_REVOLUTION: "Units.M_PER_REVOLUTION",
  },
  TIME: {
    DAY: "Units.DAY",
    HR: "Units.HR",
    MIN: "Units.MIN",
    MKS: "Units.MKS",
    MS: "Units.MS",
    S: "Units.S",
    WEEK: "Units.WEEK",
    YEAR_SIDEREAL: "Units.YEAR_SIDEREAL",
    YEAR_TROPICAL: "Units.YEAR_TROPICAL",
    YR: "Units.YR",
  },
  TORQUE: {
    KGF_M: "Units.KGF_M",
    KN_M: "Units.KN_M",
    KPF_FT: "Units.KPF_FT",
    LBF_FT: "Units.LBF_FT",
    LBF_IN: "Units.LBF_IN",
    MEGAN_M: "Units.MEGAN_M",
    N_CM: "Units.N_CM",
    N_M: "Units.N_M",
  },
  TORSIONAL_WARPING_CONSTANT: {
    CM_TO_THE_SIXTH: "Units.CM_TO_THE_SIXTH",
    FT_TO_THE_SIXTH: "Units.FT_TO_THE_SIXTH",
    IN_TO_THE_SIXTH: "Units.IN_TO_THE_SIXTH",
    M_TO_THE_SIXTH: "Units.M_TO_THE_SIXTH",
    MM_TO_THE_SIXTH: "Units.MM_TO_THE_SIXTH",
  },
  VELOCITY: {
    CM_PER_DAY: "Units.CM_PER_DAY",
    CM_PER_HR: "Units.CM_PER_HR",
    CM_PER_MIN: "Units.CM_PER_MIN",
    CM_PER_SEC: "Units.CM_PER_SEC",
    FT_PER_DAY: "Units.FT_PER_DAY",
    FT_PER_HR: "Units.FT_PER_HR",
    FT_PER_MIN: "Units.FT_PER_MIN",
    FT_PER_SEC: "Units.FT_PER_SEC",
    IN_PER_DAY: "Units.IN_PER_DAY",
    IN_PER_HR: "Units.IN_PER_HR",
    IN_PER_MIN: "Units.IN_PER_MIN",
    IN_PER_SEC: "Units.IN_PER_SEC",
    KM_PER_HR: "Units.KM_PER_HR",
    KM_PER_SEC: "Units.KM_PER_SEC",
    KNOT_INTERNATIONAL: "Units.KNOT_INTERNATIONAL",
    M_PER_DAY: "Units.M_PER_DAy",
    M_PER_HR: "Units.M_PER_HR",
    M_PER_MIN: "Units.M_PER_MIN",
    M_PER_SEC: "Units.M_PER_SEC",
    MM_PER_DAY: "Units.MM_PER_DAY",
    MM_PER_HR: "Units.MM_PER_HR",
    MM_PER_MIN: "Units.MM_PER_MIN",
    MM_PER_SEC: "Units.MM_PER_SEC",
    MPH: "Units.MPH",
    YRD_PER_SEC: "Units.YRD_PER_SEC",
  },
  VOLUME: {
    ACRE_FT: "Units.ACRE_FT",
    ACRE_IN: "Units.ACRE_IN",
    CUB_CM: "Units.CUB_CM",
    CUB_DM: "Units.CUB_DM",
    CUB_FT: "Units.CUB_FT",
    CUB_IN: "Units.CUB_IN",
    CUB_KM: "Units.CUB_KM",
    CUB_M: "Units.CUB_M",
    CUB_MILE: "Units.CUB_MILE",
    CUB_MM: "Units.CUB_MM",
    CUB_UM: "Units.CUB_UM",
    CUB_US_SURVEY_FT: "Units.CUB_US_SURVEY_FT",
    CUB_US_SURVEY_IN: "Units.CUB_US_SURVEY_IN",
    CUB_US_SURVEY_MILE: "Units.CUB_US_SURVEY_MILE",
    CUB_US_SURVEY_YRD: "Units.CUB_US_SURVEY_YRD",
    CUB_YRD: "Units.CUB_YRD",
    GALLON: "Units.GALLON",
    GALLON_IMPERIAL: "Units.GALLON_IMPERIAL",
    LITRE: "Units.LITRE",
    MICROLITRE: "Units.MICROLITRE",
    MILLILITRE: "Units.MILLILITRE",
    MILLION_GALLON: "Units.MILLION_GALLON",
    MILLION_LITRE: "Units.MILLION_LITRE",
    THOUSAND_GALLON: "Units.THOUSAND_GALLON",
    THOUSAND_LITRE: "Units.THOUSAND_LITRE",
  },
  VOLUME_PER_LENGTH: {
    CUB_FT_PER_FT: "Units.CUB_FT_PER_FT",
    CUB_M_PER_M: "Units.CUB_M_PER_M",
  },
  VOLUME_RATIO: {
    CUB_M_PER_CUB_M: "Units.CUB_M_PER_CUB_M",
    LITRE_PER_LITRE: "Units.LITRE_PER_LITRE",
  },
  VOLUMETRIC_FLOW: {
    ACRE_FT_PER_DAY: "Units.ACRE_FT_PER_DAY",
    ACRE_FT_PER_HR: "Units.ACRE_FT_PER_HR",
    ACRE_FT_PER_MIN: "Units.ACRE_FT_PER_MIN",
    ACRE_IN_PER_DAY: "Units.ACRE_IN_PER_DAY",
    ACRE_IN_PER_HR: "Units.ACRE_IN_PER_HR",
    ACRE_IN_PER_MIN: "Units.ACRE_IN_PER_MIN",
    CUB_FT_PER_DAY: "Units.CUB_FT_PER_DAY",
    CUB_FT_PER_MIN: "Units.CUB_FT_PER_MIN",
    CUB_FT_PER_SEC: "Units.CUB_FT_PER_SEC",
    CUB_IN_PER_MIN: "Units.CUB_IN_PER_MIN",
    CUB_IN_PER_SEC: "Units.CUB_IN_PER_SEC",
    CUB_M_PER_DAY: "Units.CUB_M_PER_DAY",
    CUB_M_PER_HR: "Units.CUB_M_PER_HR",
    CUB_M_PER_MIN: "Units.CUB_M_PER_MIN",
    CUB_M_PER_SEC: "Units.CUB_M_PER_SEC",
    GALLON_IMPERIAL_PER_DAY: "Units.GALLON_IMPERIAL_PER_DAY",
    GALLON_IMPERIAL_PER_HR: "Units.GALLON_IMPERIAL_PER_HR",
    GALLON_IMPERIAL_PER_MIN: "Units.GALLON_IMPERIAL_PER_MIN",
    GALLON_IMPERIAL_PER_SEC: "Units.GALLON_IMPERIAL_PER_SEC",
    GALLON_PER_DAY: "Units.GALLON_PER_DAY",
    GALLON_PER_HR: "Units.GALLON_PER_HR",
    GALLON_PER_MIN: "Units.GALLON_PER_MIN",
    GALLON_PER_SEC: "Units.GALLON_PER_SEC",
    LITRE_PER_DAY: "Units.LITRE_PER_DAY",
    LITRE_PER_HR: "Units.LITRE_PER_HR",
    LITRE_PER_MIN: "Units.LITRE_PER_MIN",
    LITRE_PER_SEC: "Units.LITRE_PER_SEC",
  },
  WORK: {
    BTU: "Units.BTU",
    FT_PDL: "Units.FT_PDL",
    GJ: "Units.GJ",
    GWH: "Units.GWH",
    J: "Units.J",
    KILOBTU: "Units.KILOBTU",
    KJ: "Units.KJ",
    KWH: "Units.KWH",
    MEGAJ: "Units.MEGAJ",
    MEGAWH: "Units.MEGAWH",
    WATT_SECOND: "Units.WATT_SECOND",
  },
} as const;

/** Canonical bundled phenomenon identifiers from the bundled Units.json asset.
 * @beta
 */
export const Phenomena = {
  ACCELERATION: "Units.ACCELERATION",
  ANGLE: "Units.ANGLE",
  ANGULAR_VELOCITY: "Units.ANGULAR_VELOCITY",
  APPARENT_POWER: "Units.APPARENT_POWER",
  AREA: "Units.AREA",
  AREA_MOMENT_INERTIA: "Units.AREA_MOMENT_INERTIA",
  AREA_SPRING_CONSTANT: "Units.AREA_SPRING_CONSTANT",
  AREA_TORQUE: "Units.AREA_TORQUE",
  CAPITA: "Units.CAPITA",
  CURRENCY: "Units.CURRENCY",
  CURRENT: "Units.CURRENT",
  DENSITY: "Units.DENSITY",
  DYNAMIC_VISCOSITY: "Units.DYNAMIC_VISCOSITY",
  ELECTRIC_CHARGE: "Units.ELECTRIC_CHARGE",
  ELECTRIC_POTENTIAL: "Units.ELECTRIC_POTENTIAL",
  ELECTRICAL_RESISTANCE: "Units.ELECTRICAL_RESISTANCE",
  ELECTRICAL_RESISTIVITY: "Units.ELECTRICAL_RESISTIVITY",
  ENERGY_DENSITY: "Units.ENERGY_DENSITY",
  ENTROPY: "Units.ENTROPY",
  FLOW_DENSITY_PER_AREA: "Units.FLOW_DENSITY_PER_AREA",
  FORCE: "Units.FORCE",
  FORCE_DENSITY: "Units.FORCE_DENSITY",
  FORCE_DISPLAY_SCALE: "Units.FORCE_DISPLAY_SCALE",
  FREQUENCY: "Units.FREQUENCY",
  HEAT_FLUX_DENSITY: "Units.HEAT_FLUX_DENSITY",
  HEAT_TRANSFER: "Units.HEAT_TRANSFER",
  HORIZONTAL_DIRECTION: "Units.HORIZONTAL_DIRECTION",
  ILLUMINANCE: "Units.ILLUMINANCE",
  KINEMATIC_VISCOSITY: "Units.KINEMATIC_VISCOSITY",
  LENGTH: "Units.LENGTH",
  LENGTH_RATIO: "Units.LENGTH_RATIO",
  LINEAR_COEFFICIENT_OF_THERMAL_EXPANSION: "Units.LINEAR_COEFFICIENT_OF_THERMAL_EXPANSION",
  LINEAR_DENSITY: "Units.LINEAR_DENSITY",
  LINEAR_LOAD: "Units.LINEAR_LOAD",
  LINEAR_RATE: "Units.LINEAR_RATE",
  LINEAR_ROTATIONAL_SPRING_CONSTANT: "Units.LINEAR_ROTATIONAL_SPRING_CONSTANT",
  LINEAR_SPRING_CONSTANT: "Units.LINEAR_SPRING_CONSTANT",
  LINEAR_TORQUE: "Units.LINEAR_TORQUE",
  LUMINOSITY: "Units.LUMINOSITY",
  LUMINOUS_EFFICACY: "Units.LUMINOUS_EFFICACY",
  LUMINOUS_FLUX: "Units.LUMINOUS_FLUX",
  MASS: "Units.MASS",
  MASS_FLOW: "Units.MASS_FLOW",
  MASS_RATIO: "Units.MASS_RATIO",
  MOLAR_CONCENTRATION: "Units.MOLAR_CONCENTRATION",
  MOLAR_FLOW: "Units.MOLAR_FLOW",
  MOLAR_VOLUME: "Units.MOLAR_VOLUME",
  MOLE: "Units.MOLE",
  MOMENT_DISPLAY_SCALE: "Units.MOMENT_DISPLAY_SCALE",
  NUMBER: "Units.NUMBER",
  PERCENTAGE: "Units.PERCENTAGE",
  PIPE_DIAMETER_LENGTH: "Units.PIPE_DIAMETER_LENGTH",
  POPULATION_DENSITY: "Units.POPULATION_DENSITY",
  POWER: "Units.POWER",
  PRESSURE: "Units.PRESSURE",
  PRESSURE_GRADIENT: "Units.PRESSURE_GRADIENT",
  PROBABILITY: "Units.PROBABILITY",
  ROTATIONAL_SPRING_CONSTANT: "Units.ROTATIONAL_SPRING_CONSTANT",
  SLOPE: "Units.SLOPE",
  SOLIDANGLE: "Units.SOLIDANGLE",
  SPECIFIC_ENERGY: "Units.SPECIFIC_ENERGY",
  SPECIFIC_HEAT_CAPACITY: "Units.SPECIFIC_HEAT_CAPACITY",
  SPECIFIC_HEAT_CAPACITY_MOLAR: "Units.SPECIFIC_HEAT_CAPACITY_MOLAR",
  SPECIFIC_VOLUME: "Units.SPECIFIC_VOLUME",
  SPRING_CONSTANT: "Units.SPRING_CONSTANT",
  SURFACE_DENSITY: "Units.SURFACE_DENSITY",
  TEMPERATURE: "Units.TEMPERATURE",
  TEMPERATURE_CHANGE: "Units.TEMPERATURE_CHANGE",
  TEMPERATURE_GRADIENT: "Units.TEMPERATURE_GRADIENT",
  THERMAL_CONDUCTIVITY: "Units.THERMAL_CONDUCTIVITY",
  THERMAL_INSULANCE: "Units.THERMAL_INSULANCE",
  THREAD_PITCH: "Units.THREAD_PITCH",
  TIME: "Units.TIME",
  TORQUE: "Units.TORQUE",
  TORSIONAL_WARPING_CONSTANT: "Units.TORSIONAL_WARPING_CONSTANT",
  VELOCITY: "Units.VELOCITY",
  VOLUME: "Units.VOLUME",
  VOLUME_PER_LENGTH: "Units.VOLUME_PER_LENGTH",
  VOLUME_RATIO: "Units.VOLUME_RATIO",
  VOLUMETRIC_FLOW: "Units.VOLUMETRIC_FLOW",
  WORK: "Units.WORK",
} as const;

/** Canonical bundled unit system identifiers from the bundled Units.json asset.
 * @beta
 */
export const UnitSystems = {
  CGS: "Units.CGS",
  CONSTANT: "Units.CONSTANT",
  FINANCE: "Units.FINANCE",
  IMPERIAL: "Units.IMPERIAL",
  INDUSTRIAL: "Units.INDUSTRIAL",
  INTERNATIONAL: "Units.INTERNATIONAL",
  MARITIME: "Units.MARITIME",
  METRIC: "Units.METRIC",
  SI: "Units.SI",
  STATISTICS: "Units.STATISTICS",
  USCUSTOM: "Units.USCUSTOM",
  USSURVEY: "Units.USSURVEY",
} as const;

/** Bundled canonical unit-name identifiers generated from Units.
 * @beta
 */
export type UnitName = NestedValueOf<typeof Units>;

/** Bundled canonical phenomenon-name identifiers generated from Phenomena.
 * @beta
 */
export type PhenomenonName = ValueOf<typeof Phenomena>;

/** Bundled canonical unit-system identifiers generated from UnitSystems.
 * @beta
 */
export type UnitSystemName = ValueOf<typeof UnitSystems>;
